import React from 'react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import { Link } from 'react-router-dom';

const AboutPage = () => {
  return (
    <div className="min-h-screen flex flex-col bg-background-light">
      <Navigation />

      {/* Hero Section */}
      <section className="bg-primary-500 text-white py-16">
        <div className="container mx-auto px-4">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">About WealthQuota Investment Group</h1>
          <p className="text-xl text-primary-100 max-w-3xl">
            Building financial futures through strategic investment solutions since 1999.
          </p>
        </div>
      </section>
      
      {/* Our Story */}
      <section className="py-16 bg-background-paper">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row gap-12 items-center">
            <div className="md:w-1/2">
              <h2 className="text-3xl font-bold text-primary-500 mb-6">Our Story</h2>
              <p className="text-neutral-700 mb-4">
                Founded in 1999, WealthQuota Investment Group emerged from a vision to transform how individuals and businesses approach wealth management. We recognized that traditional investment models often failed to provide personalized solutions that truly aligned with clients' long-term goals.
              </p>
              <p className="text-neutral-700 mb-4">
                What began as a boutique advisory firm has evolved into a comprehensive investment group with a global perspective, yet we maintain the personalized approach that set us apart from the beginning.
              </p>
              <p className="text-neutral-700">
                Our name, WealthQuota, reflects our philosophy that each client has unique financial objectives that require tailored investment strategies—not standardized quotas or one-size-fits-all approaches.
                  </p>
                </div>
            <div className="md:w-1/2">
              <div className="rounded-lg overflow-hidden shadow-xl">
                <img 
                  src="/images/abt.jpg" 
                  alt="WealthQuota Office Building" 
                  className="w-full h-64 object-cover"
                />
              </div>
                </div>
              </div>
            </div>
      </section>
      
      {/* Mission & Values */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-primary-500 mb-3">Our Mission & Values</h2>
            <p className="text-neutral-600 max-w-2xl mx-auto">
              We're driven by core principles that guide our investment decisions and client relationships.
            </p>
                    </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-lg shadow-lg">
              <div className="w-12 h-12 bg-secondary-500 rounded-full flex items-center justify-center mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-primary-500 mb-2">Strategic Innovation</h3>
              <p className="text-neutral-600">
                We continuously evolve our investment strategies to capitalize on emerging opportunities while managing risk effectively.
              </p>
            </div>

            <div className="bg-white p-8 rounded-lg shadow-lg">
              <div className="w-12 h-12 bg-secondary-500 rounded-full flex items-center justify-center mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-primary-500 mb-2">Unwavering Integrity</h3>
              <p className="text-neutral-600">
                We maintain the highest ethical standards in all our dealings, fostering transparency and trust with our clients.
              </p>
              </div>
            
            <div className="bg-white p-8 rounded-lg shadow-lg">
              <div className="w-12 h-12 bg-secondary-500 rounded-full flex items-center justify-center mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-primary-500 mb-2">Client Partnership</h3>
              <p className="text-neutral-600">
                We view each client relationship as a long-term partnership, committed to understanding your unique goals and aspirations.
              </p>
            </div>
          </div>
        </div>
      </section>
      
      {/* Our Approach */}
      <section className="py-16 bg-neutral-100">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row gap-10">
            <div className="md:w-1/2">
              <h2 className="text-3xl font-bold text-primary-500 mb-6">Our Investment Approach</h2>
              <p className="text-neutral-700 mb-6">
                At WealthQuota, we employ a disciplined investment methodology that balances proven strategies with innovative approaches tailored to each client's risk tolerance and objectives.
              </p>
              
              <div className="space-y-4">
                <div className="flex items-start">
                  <div className="mt-1 mr-4 w-6 h-6 bg-accent-500 rounded-full flex items-center justify-center text-white text-sm">1</div>
                  <div>
                    <h3 className="text-lg font-semibold text-primary-500">Comprehensive Analysis</h3>
                    <p className="text-neutral-600">We conduct thorough market analysis and due diligence to identify opportunities with optimal risk-adjusted returns.</p>
                  </div>
            </div>

                <div className="flex items-start">
                  <div className="mt-1 mr-4 w-6 h-6 bg-accent-500 rounded-full flex items-center justify-center text-white text-sm">2</div>
              <div>
                    <h3 className="text-lg font-semibold text-primary-500">Strategic Diversification</h3>
                    <p className="text-neutral-600">We create portfolios with strategic asset allocation across multiple sectors, regions, and investment vehicles.</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="mt-1 mr-4 w-6 h-6 bg-accent-500 rounded-full flex items-center justify-center text-white text-sm">3</div>
              <div>
                    <h3 className="text-lg font-semibold text-primary-500">Active Management</h3>
                    <p className="text-neutral-600">Our team continuously monitors and adjusts portfolios in response to changing market conditions and emerging opportunities.</p>
                        </div>
                      </div>
                
                <div className="flex items-start">
                  <div className="mt-1 mr-4 w-6 h-6 bg-accent-500 rounded-full flex items-center justify-center text-white text-sm">4</div>
                      <div>
                    <h3 className="text-lg font-semibold text-primary-500">Performance Transparency</h3>
                    <p className="text-neutral-600">We provide clear, detailed reporting on portfolio performance and progress toward financial goals.</p>
                    </div>
                </div>
              </div>
            </div>

            <div className="md:w-1/2">
              <div className="bg-white p-8 rounded-lg shadow-lg">
                <h3 className="text-2xl font-bold text-primary-500 mb-4">Investment Philosophy</h3>
                <p className="text-neutral-600 mb-6">
                  Our investment decisions are guided by four fundamental principles that have consistently delivered value to our clients through various market cycles:
                </p>
                
                <ul className="space-y-4">
                  <li className="p-4 border-l-4 border-secondary-500 bg-neutral-50">
                    <h4 className="font-semibold text-primary-600">Long-Term Perspective</h4>
                    <p className="text-neutral-600 text-sm">We focus on sustainable growth rather than short-term fluctuations.</p>
                  </li>
                  
                  <li className="p-4 border-l-4 border-secondary-500 bg-neutral-50">
                    <h4 className="font-semibold text-primary-600">Risk Management</h4>
                    <p className="text-neutral-600 text-sm">We prioritize capital preservation alongside growth opportunities.</p>
                  </li>
                  
                  <li className="p-4 border-l-4 border-secondary-500 bg-neutral-50">
                    <h4 className="font-semibold text-primary-600">Data-Driven Decisions</h4>
                    <p className="text-neutral-600 text-sm">Our strategies are informed by rigorous research and analysis.</p>
                  </li>
                  
                  <li className="p-4 border-l-4 border-secondary-500 bg-neutral-50">
                    <h4 className="font-semibold text-primary-600">Value Identification</h4>
                    <p className="text-neutral-600 text-sm">We seek investments with intrinsic value beyond market pricing.</p>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* CTA Section */}
      <section className="py-16 bg-primary-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-4">Ready to Begin Your Investment Journey?</h2>
          <p className="text-xl text-primary-100 mb-8 max-w-2xl mx-auto">
            Let's discuss how WealthQuota can help you achieve your financial objectives with strategies tailored to your unique situation.
          </p>
          <div className="flex justify-center">
            <Link to="/contact">
              <button className="bg-secondary-500 hover:bg-secondary-600 text-white py-3 px-8 rounded-lg font-medium transition-all duration-300 hover:shadow-lg hover:-translate-y-1 transform">
                Schedule a Consultation
              </button>
            </Link>
          </div>
        </div>
      </section>
      
      {/* Certifications & Recognitions */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-primary-500 mb-3">Certifications & Recognition</h2>
            <p className="text-neutral-600 max-w-2xl mx-auto">
              WealthQuota is proud to be recognized by leading financial authorities and organizations.
              </p>
            </div>

          <div className="flex flex-wrap justify-center items-center gap-12">
            {/* ISO Certification */}
            <div className="w-32 h-20 bg-gradient-to-br from-neutral-50 to-neutral-100 flex items-center justify-center rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border border-neutral-200">
              <svg className="w-24 h-16" viewBox="0 0 100 70" fill="none" xmlns="http://www.w3.org/2000/svg">
                <defs>
                  <linearGradient id="isoGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#1B3160" />
                    <stop offset="100%" stopColor="#2A4A8F" />
                  </linearGradient>
                  <filter id="glow">
                    <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
                    <feMerge>
                      <feMergeNode in="coloredBlur"/>
                      <feMergeNode in="SourceGraphic"/>
                    </feMerge>
                  </filter>
                </defs>
                <path d="M10,20 L90,20 L90,50 L10,50 Z" fill="url(#isoGrad)" filter="url(#glow)"/>
                <circle cx="50" cy="35" r="22" stroke="white" strokeWidth="2" strokeDasharray="4 2"/>
                <text x="50" y="32" fontFamily="Arial" fontSize="16" fill="white" textAnchor="middle" fontWeight="bold">ISO 9001</text>
                <text x="50" y="45" fontFamily="Arial" fontSize="8" fill="white" textAnchor="middle" letterSpacing="2">CERTIFIED</text>
                <path d="M15,20 L25,15 L85,15 L95,20" stroke="white" strokeWidth="0.5" fill="none"/>
              </svg>
                  </div>

            {/* SEC Registration */}
            <div className="w-32 h-20 bg-gradient-to-br from-neutral-50 to-neutral-100 flex items-center justify-center rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border border-neutral-200">
              <svg className="w-24 h-16" viewBox="0 0 100 70" fill="none" xmlns="http://www.w3.org/2000/svg">
                <defs>
                  <linearGradient id="secGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#B8860B" />
                    <stop offset="100%" stopColor="#DAA520" />
                  </linearGradient>
                  <filter id="secGlow">
                    <feGaussianBlur stdDeviation="1.5" result="coloredBlur"/>
                    <feMerge>
                      <feMergeNode in="coloredBlur"/>
                      <feMergeNode in="SourceGraphic"/>
                    </feMerge>
                  </filter>
                </defs>
                <circle cx="50" cy="35" r="25" fill="url(#secGrad)" filter="url(#secGlow)"/>
                <circle cx="50" cy="35" r="22" stroke="white" strokeWidth="0.5" strokeDasharray="3 3"/>
                <path d="M35,35 C35,25 65,25 65,35 C65,45 35,45 35,35" stroke="white" strokeWidth="2" fill="none"/>
                <text x="50" y="32" fontFamily="Arial" fontSize="14" fill="white" textAnchor="middle" fontWeight="bold">SEC</text>
                <text x="50" y="45" fontFamily="Arial" fontSize="8" fill="white" textAnchor="middle" letterSpacing="1">REGISTERED</text>
              </svg>
                  </div>

            {/* FINRA Member */}
            <div className="w-32 h-20 bg-gradient-to-br from-neutral-50 to-neutral-100 flex items-center justify-center rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border border-neutral-200">
              <svg className="w-24 h-16" viewBox="0 0 100 70" fill="none" xmlns="http://www.w3.org/2000/svg">
                <defs>
                  <linearGradient id="finraGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#1B3160" />
                    <stop offset="100%" stopColor="#2A4A8F" />
                  </linearGradient>
                  <filter id="finraGlow">
                    <feGaussianBlur stdDeviation="1" result="coloredBlur"/>
                    <feMerge>
                      <feMergeNode in="coloredBlur"/>
                      <feMergeNode in="SourceGraphic"/>
                    </feMerge>
                  </filter>
                </defs>
                <path d="M20,35 L50,15 L80,35 L50,55 Z" fill="url(#finraGrad)" filter="url(#finraGlow)"/>
                <path d="M25,35 L50,20 L75,35 L50,50 Z" stroke="white" strokeWidth="0.5" fill="none"/>
                <text x="50" y="32" fontFamily="Arial" fontSize="14" fill="white" textAnchor="middle" fontWeight="bold">FINRA</text>
                <text x="50" y="42" fontFamily="Arial" fontSize="8" fill="white" textAnchor="middle" letterSpacing="1">MEMBER</text>
                <circle cx="50" cy="35" r="3" fill="white"/>
              </svg>
            </div>

            {/* Best Investment Firm Award */}
            <div className="w-32 h-20 bg-gradient-to-br from-neutral-50 to-neutral-100 flex items-center justify-center rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border border-neutral-200">
              <svg className="w-24 h-16" viewBox="0 0 100 70" fill="none" xmlns="http://www.w3.org/2000/svg">
                <defs>
                  <linearGradient id="awardGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#B8860B" />
                    <stop offset="100%" stopColor="#DAA520" />
                  </linearGradient>
                  <filter id="awardGlow">
                    <feGaussianBlur stdDeviation="1" result="coloredBlur"/>
                    <feMerge>
                      <feMergeNode in="coloredBlur"/>
                      <feMergeNode in="SourceGraphic"/>
                    </feMerge>
                  </filter>
                </defs>
                <path d="M50,10 L60,30 L82,30 L65,45 L72,65 L50,52 L28,65 L35,45 L18,30 L40,30 Z" fill="url(#awardGrad)" filter="url(#awardGlow)"/>
                <path d="M50,15 L58,31 L77,31 L62,44 L68,60 L50,49 L32,60 L38,44 L23,31 L42,31 Z" stroke="white" strokeWidth="0.5" fill="none"/>
                <circle cx="50" cy="35" r="12" stroke="white" strokeWidth="0.5" fill="none"/>
                <text x="50" y="32" fontFamily="Arial" fontSize="10" fill="white" textAnchor="middle" fontWeight="bold">EXCELLENCE</text>
                <text x="50" y="42" fontFamily="Arial" fontSize="8" fill="white" textAnchor="middle">IN INVESTMENT</text>
                <text x="50" y="50" fontFamily="Arial" fontSize="8" fill="white" textAnchor="middle">2023</text>
              </svg>
            </div>

            {/* Trusted Partner Recognition */}
            <div className="w-32 h-20 bg-gradient-to-br from-neutral-50 to-neutral-100 flex items-center justify-center rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border border-neutral-200">
              <svg className="w-24 h-16" viewBox="0 0 100 70" fill="none" xmlns="http://www.w3.org/2000/svg">
                <defs>
                  <linearGradient id="trustGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#1B3160" />
                    <stop offset="100%" stopColor="#2A4A8F" />
                  </linearGradient>
                  <filter id="trustGlow">
                    <feGaussianBlur stdDeviation="1.5" result="coloredBlur"/>
                    <feMerge>
                      <feMergeNode in="coloredBlur"/>
                      <feMergeNode in="SourceGraphic"/>
                    </feMerge>
                  </filter>
                </defs>
                <path d="M25,15 H75 V55 H25 Z" rx="10" fill="url(#trustGrad)" filter="url(#trustGlow)"/>
                <path d="M30,20 H70 V50 H30 Z" rx="8" stroke="white" strokeWidth="0.5" fill="none"/>
                <path d="M35,35 C35,25 65,25 65,35 S35,45 35,35" stroke="white" strokeWidth="1" fill="none"/>
                <circle cx="50" cy="35" r="15" stroke="white" strokeWidth="0.5" strokeDasharray="3 2" fill="none"/>
                <text x="50" y="32" fontFamily="Arial" fontSize="12" fill="white" textAnchor="middle" fontWeight="bold">TRUSTED</text>
                <text x="50" y="42" fontFamily="Arial" fontSize="8" fill="white" textAnchor="middle" letterSpacing="1">PARTNER</text>
              </svg>
            </div>
          </div>
            </div>
      </section>
      
      <Footer />
                  </div>
  );
};

export default AboutPage;